import { GALLERY_TABS, type GalleryTab } from './state';
import { tabToPath } from './url-state';

/**
 * Préférence UTILISATEUR : DERNIER ONGLET de galerie visité (Galerie / Dossiers /
 * Tags), stockée côté client en `localStorage` — même patron que la densité
 * (`density-pref.ts`), le tri des cartes de dossier (`folder-sort-pref.ts`) et l'ordre
 * de tri (`default-sort-pref.ts`).
 *
 * À QUOI ELLE SERT (XPHOTO-32, lot 1) : la Maison « Accueil » du fil d'Ariane pointait
 * en dur sur `/`, si bien que l'application repartait TOUJOURS sur la Galerie — alors
 * que quelqu'un qui travaille dans « Dossiers » y revient session après session. La
 * Maison mène désormais à l'onglet mémorisé.
 *
 * ELLE NE RÉÉCRIT JAMAIS L'URL. La préférence n'agit QUE sur la CIBLE du lien Maison :
 * aucun `navigate()` déclenché par un effet, aucune redirection (régression v1.21.3 —
 * l'URL est la source de vérité et n'est jamais réécrite par effet de bord). Qui
 * arrive sur `/` reste sur `/`.
 *
 * Elle reste HORS de l'URL à dessein : ce n'est pas un « emplacement » adressable —
 * un lien partagé doit ouvrir l'onglet qu'il décrit, pas celui du visiteur.
 *
 * Module PUR & testé, tolérant si `localStorage` est indisponible (mode privé strict,
 * quotas) : repli en mémoire, jamais d'exception.
 */

/** Clé de persistance (namespace `xphoto.*`, comme la densité). */
export const TAB_PREF_STORAGE_KEY = 'xphoto.gallery.tab';

/** Défaut de fabrique : la Galerie (comportement historique de la Maison). */
export const TAB_PREF_FALLBACK: GalleryTab = 'gallery';

/** Repli mémoire quand `localStorage` est inaccessible (jamais d'exception). */
let memoryPref: GalleryTab | null = null;

/** Valide une valeur brute contre l'allow-list des onglets, ou `null`. */
function parsePref(raw: unknown): GalleryTab | null {
  return typeof raw === 'string' && (GALLERY_TABS as readonly string[]).includes(raw)
    ? (raw as GalleryTab)
    : null;
}

/**
 * Onglet dont `pathname` est EXACTEMENT la base (`/` → Galerie, `/dossiers` →
 * Dossiers, `/tags` → Tags), sinon `null`. Seul écart toléré : la barre oblique finale
 * (`/dossiers/`), normalisée comme dans `pathToTab`. La table des chemins n'est PAS
 * redite ici — elle est dérivée de la seule qui existe (`tabToPath`, `url-state.ts`).
 *
 * À NE PAS CONFONDRE avec `pathToTab`, qui fait retomber TOUT chemin inconnu sur
 * `gallery`, `/photo/:id` compris : mémoriser d'après `pathToTab` écraserait la
 * préférence avec « Galerie » dès qu'on ouvre une photo depuis l'onglet Dossiers.
 * C'est le CHEMIN EXACT qui décide, pas l'onglet dérivé.
 */
export function tabForExactPath(pathname: string): GalleryTab | null {
  const normalized = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return GALLERY_TABS.find((tab) => tabToPath(tab) === normalized) ?? null;
}

/**
 * Lit la préférence persistée, ou `null` si absente/invalide. Le repli MÉMOIRE n'est
 * consulté QUE si `localStorage` est INDISPONIBLE — pas quand il renvoie simplement
 * `null` : vider le stockage doit bien remettre au défaut (même discipline que la
 * densité et le tri).
 */
export function readStoredTab(): GalleryTab | null {
  try {
    if (typeof localStorage === 'undefined') return memoryPref;
    return parsePref(localStorage.getItem(TAB_PREF_STORAGE_KEY));
  } catch {
    return memoryPref;
  }
}

/** Onglet mémorisé EFFECTIF : persisté sinon le défaut de fabrique (Galerie). */
export function getTabPref(): GalleryTab {
  return readStoredTab() ?? TAB_PREF_FALLBACK;
}

/** Persiste l'onglet (repli mémoire + `localStorage`, silencieux si indisponible). */
export function storeTab(tab: GalleryTab): void {
  const valid = parsePref(tab);
  if (valid === null) return;
  memoryPref = valid;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(TAB_PREF_STORAGE_KEY, valid);
  } catch {
    /* stockage indisponible : la préférence mémoire fait foi pour la session. */
  }
}

/**
 * Mémorise l'onglet d'un chemin — NO-OP si `pathname` n'est pas EXACTEMENT une base
 * d'onglet. Déclencher sur le CHEMIN (et non sur un clic) couvre d'un seul geste le
 * clic sur un onglet, le clic sur le fil d'Ariane et l'arrivée par lien direct.
 */
export function rememberTabForPath(pathname: string): void {
  const tab = tabForExactPath(pathname);
  if (tab !== null) storeTab(tab);
}

/**
 * Onglet visé par le lien Maison depuis `pathname`.
 *
 * Sur une route d'ONGLET, c'est l'onglet COURANT — celui-là même que l'on vient de
 * mémoriser. On ne le relit donc PAS du stockage : l'écriture a lieu dans un effet
 * (après le rendu), si bien qu'une lecture pendant le rendu renverrait encore la
 * valeur précédente et laisserait le lien Maison en retard d'une navigation. Partout
 * ailleurs (Admin, Paramètres, Outils, visionneuse…), la préférence persistée fait foi.
 */
export function homeLinkTab(pathname: string): GalleryTab {
  return tabForExactPath(pathname) ?? getTabPref();
}
